import React, { useState, useEffect, useRef } from 'react';
import { WebsiteDesignProps } from './types';
import { 
  Leaf, Sun, Wind, CloudRain, Flower2, Droplets, ArrowRight, ArrowUpRight, 
  Menu, X, Zap, Heart, Users, Trophy, School, Clock, 
  MapPin, Phone, Mail, Globe, GraduationCap, ChevronLeft, 
  ChevronRight, ChevronDown, Download, Send, Plus, Layout, Sparkles, 
  FileText, Instagram, Facebook, Linkedin, Youtube, Quote, BookOpen, Star, Camera, Bird, Mountain
} from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { Button } from "@/components/ui/button";
import { VisualImageEditable } from "@/components/VisualImageEditable";
import { VisualTextEditable } from "@/components/VisualTextEditable";

const EcoFriendly: React.FC<WebsiteDesignProps> = ({ previewData, isEditMode, onUpdateField }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Icon resolver helper
  const getIcon = (name: string | any) => {
      if (!name) return Leaf;
      if (typeof name !== 'string') return name;
      // @ts-ignore
      const Icon = LucideIcons[name];
      return Icon || Leaf;
  };

  const schoolName = previewData?.name || "Terra Verde School";

  // Shared Data Model (Strict Matching Design 1)
  const defaultData = {
    hero: {
      images: ["https://images.unsplash.com/photo-1588072432836-e10032774350?q=80&w=2072&auto=format&fit=crop"],
      tagline: "Sustainable Living & Learning",
      titlePrefix: "Rooted in Nature",
      subtitle: "Join a community dedicated to academic mastery and environmental stewardship, set within an organic living campus."
    },
    features: [
      { icon: "Sun", title: "Solar Powered", desc: "Our entire campus runs on 100% renewable solar and wind energy." },
      { icon: "Droplets", title: "Pure Systems", desc: "Advanced rainwater harvesting and greywater recycling protocols." },
      { icon: "Bird", title: "Biodiversity Hub", desc: "A living laboratory with over 200 species of local flora and fauna." }
    ],
    programs: [
      { title: "Eco-Explorers", age: "Years 1-5", desc: "Foundational learning integrated with forest school techniques.", img: "https://images.unsplash.com/photo-1526666361175-e359464391af?q=80&w=2072&auto=format&fit=crop" },
      { title: "Bioscience Lab", age: "Years 10-13", desc: "Advanced environmental science and sustainable engineering.", img: "https://images.unsplash.com/photo-1574943320219-553eb213f729?q=80&w=2086&auto=format&fit=crop" }
    ],
    stats: [
        { value: "0Kg", label: "CARBON FOOTPRINT" },
        { value: "85%", label: "OWN FOOD GROWN" },
        { value: "100%", label: "PLASTIC FREE" }
    ],
    news: [
        { title: "Harvest Festival 2026", date: "MAY 15", desc: "Our annual celebration of the campus organic farm output.", img: "https://images.unsplash.com/photo-1516253593875-bd7ba052fbc5?q=80&w=2070&auto=format&fit=crop" }
    ],
    nav: [
        { label: 'Ecosystem', href: '#features' },
        { label: 'Pathways', href: '#programs' },
        { label: 'Sanctuary', href: '#gallery' },
        { label: 'Coordinates', href: '#contact' }
    ],
    team: [
        { name: "Gaia Rivera", role: "Environmental Dean", img: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?q=80&w=1976&auto=format&fit=crop" },
        { name: "Silas Green", role: "Sustainability Lead", img: "https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?q=80&w=1974&auto=format&fit=crop" }
    ],
    gallery: [
        { category: "Flora", img: "https://images.unsplash.com/photo-1466692476868-aef1dfb1e735?q=80&w=2070&auto=format&fit=crop", title: "Botanical Hall" },
        { category: "Farm", img: "https://images.unsplash.com/photo-1500382017468-9049fed747ef?q=80&w=2089&auto=format&fit=crop", title: "Organic Fields" }
    ],
    facilities: [
        { title: "Sun Arena", desc: "Outdoor performance stage integrated into the natural landscape.", img: "https://images.unsplash.com/photo-1441974231531-c6227db76b6e?q=80&w=2071&auto=format&fit=crop", icon: "Sun" },
        { title: "Leaf Library", desc: "Quiet study library built from sustainable local timber.", img: "https://images.unsplash.com/photo-1524995997946-a1c2e315a42f?q=80&w=2070&auto=format&fit=crop", icon: "BookOpen" }
    ]
  };

  const mockData = {
      ...previewData,
      hero: {
          titlePrefix: previewData?.hero?.titlePrefix || defaultData.hero.titlePrefix,
          tagline: previewData?.hero?.tagline || defaultData.hero.tagline,
          subtitle: previewData?.hero?.subtitle || defaultData.hero.subtitle,
          images: previewData?.hero?.images || defaultData.hero.images,
          backgroundImage: previewData?.hero?.backgroundImage || defaultData.hero.images[0]
      },
      about: {
          title: previewData?.about?.title || "Growing in Wisdom.",
          description: previewData?.about?.description || schoolName + " is a regenerative learning community where we recognize that the health of the student and the health of the soil are one and the same.",
          sectionImage: previewData?.about?.sectionImage || "https://images.unsplash.com/photo-1530219047640-620216641577?q=80&w=2070&auto=format&fit=crop"
      },
      features: (previewData?.features || defaultData.features).map((f: any) => ({ 
        ...f, 
        icon: getIcon(f.icon),
        desc: f.description || f.desc || ""
      })),
      programs: (previewData?.programs || defaultData.programs).map((p: any) => ({
        title: p.name || p.title || "",
        age: p.age || "",
        desc: p.description || p.desc || "",
        img: p.image || p.img || ""
      })),
      stats: (previewData?.stats || defaultData.stats).map((s: any) => ({
          value: s.value || "0",
          label: s.label || ""
      })),
      news: (previewData?.news || defaultData.news).map((n: any) => ({
        title: n.title || "",
        date: n.date || "",
        desc: n.description || n.desc || "",
        img: n.image || n.img || ""
      })),
      team: (previewData?.team || defaultData.team).map((t: any) => ({
        name: t.name || "",
        role: t.role || "",
        img: t.img || t.image || "",
        linkedin: t.linkedin || "",
        email: t.email || ""
      })),
      gallery: (previewData?.gallery || defaultData.gallery).map((g: any) => ({
        category: g.category || "General",
        title: g.caption || g.title || "",
        img: g.image || g.img || ""
      })),
      facilities: (previewData?.facilities || defaultData.facilities).map((f: any) => ({ 
        ...f, 
        icon: getIcon(f.icon),
        desc: f.description || f.desc || "",
        img: f.image || f.img || ""
      })),
      downloads: (previewData?.downloads || [
          { title: "Impact_Report_2026.pdf", size: "1.2 MB", type: "PDF" },
          { title: "Organic_Curriculum.pdf", size: "4.5 MB", type: "PDF" }
      ]).map((d: any) => ({
          title: d.title || "",
          size: d.size || "",
          type: d.type || "PDF"
      })),
      contact: {
        address: previewData?.contact?.address || "Terra Valley Loop, Nairobi, Kenya",
        phone: previewData?.contact?.phone || "+254 000 777 888",
        email: previewData?.contact?.email || "nature@terraverde.edu",
        officeHours: previewData?.contact?.officeHours || "Sunrise to Sunset",
        facebook: previewData?.contact?.facebook || "",
        instagram: previewData?.contact?.instagram || "",
        youtube: previewData?.contact?.youtube || "",
        linkedin: previewData?.contact?.linkedin || ""
      },
      sectionVisibility: {
          hero: previewData?.sectionVisibility?.hero !== false,
          about: previewData?.sectionVisibility?.about !== false,
          stats: previewData?.sectionVisibility?.stats !== false,
          features: previewData?.sectionVisibility?.features !== false,
          programs: previewData?.sectionVisibility?.programs !== false,
          facilities: previewData?.sectionVisibility?.facilities !== false,
          team: previewData?.sectionVisibility?.team !== false,
          gallery: previewData?.sectionVisibility?.gallery !== false,
          news: previewData?.sectionVisibility?.news !== false,
          downloads: previewData?.sectionVisibility?.downloads !== false,
          cta: previewData?.sectionVisibility?.cta !== false,
          contact: previewData?.sectionVisibility?.contact !== false
      },
      nav: (previewData?.nav || defaultData.nav).map((n: any) => ({
          label: n.label || "",
          href: n.href || "#",
          visible: n.visible !== false
      })),
      labels: {
          downloadsSection: previewData?.labels?.downloadsSection || "Seed Library",
          downloadsTitle: previewData?.labels?.downloadsTitle || "Digital Resources",
          contactFormTitle: previewData?.labels?.contactFormTitle || "Start a Conversation",
          contactFormSubtitle: previewData?.labels?.contactFormSubtitle || "Reach out to our eco-learning team for admissions, tours, or general stewardship inquiries."
      }
  };

  useEffect(() => {
    const doc = containerRef.current?.ownerDocument || document;
    const root = doc.documentElement;
    if (previewData?.theme) {
        const { colors, fonts } = previewData.theme;
        root.style.setProperty('--color-primary', colors.primary || '#5c6b45');
        root.style.setProperty('--color-secondary', colors.secondary || '#8c7b6c');
        root.style.setProperty('--color-accent', colors.accent || '#f3ead3');
        root.style.setProperty('--color-background', colors.background || '#faf9f6');
        root.style.setProperty('--color-text', colors.text || '#2c331f');
        root.style.setProperty('--font-heading', `'${fonts.heading || 'Outfit'}', sans-serif`);
        root.style.setProperty('--font-body', `'${fonts.body || 'Inter'}', sans-serif`);
    }
    const fontLink = doc.createElement('link');
    fontLink.href = "https://fonts.googleapis.com/css2?family=Outfit:wght@600;800;900&family=Inter:wght@300;400;600&display=swap";
    fontLink.rel = 'stylesheet';
    doc.head.appendChild(fontLink);
  }, [previewData?.theme]);

  const handleUpdate = (path: string, val: any) => onUpdateField && onUpdateField(path, val);
  const addArrayItem = (path: string, newItem: any) => {
    if (onUpdateField) {
        const arr = [...((mockData as any)[path] || [])];
        onUpdateField(path, [...arr, newItem]);
    }
  };
  const removeArrayItem = (path: string, index: number) => {
    if (onUpdateField && window.confirm('Return this element to the earth?')) {
        const arr = [...((mockData as any)[path] || [])];
        onUpdateField(path, arr.filter((_, i) => i !== index));
    }
  };

  return (
    <div ref={containerRef} className="eco-friendly bg-[var(--color-background)] text-[var(--color-text)] min-h-screen font-[var(--font-body)] selection:bg-[var(--color-primary)] selection:text-white overflow-x-hidden">
      
      {/* Texture Background Overlay */}
      <div className="fixed inset-0 pointer-events-none opacity-5 mix-blend-multiply bg-[url('https://www.transparenttextures.com/patterns/natural-paper.png')]" />

      {/* Organic Header */}
      <nav className="container mx-auto px-6 py-8 flex justify-between items-center relative z-50">
          <div className="flex items-center gap-4 group cursor-pointer">
               <div className="w-12 h-12 bg-[var(--color-primary)]/10 rounded-[40%] flex items-center justify-center p-2 group-hover:rotate-12 transition-transform">
                   <Leaf className="text-[var(--color-primary)]" size={28} />
               </div>
               <span className="text-3xl font-black tracking-tight font-[var(--font-heading)] uppercase text-[var(--color-text)]">
                   <VisualTextEditable value={schoolName} onSave={(v) => handleUpdate('name', v)} isEditMode={!!isEditMode} />
               </span>
          </div>
          
          <div className="hidden lg:flex items-center gap-12">
              <div className="flex gap-10 text-[11px] font-bold uppercase tracking-[0.2em]">
                  {mockData.nav.filter(n => n.visible).map((n, i) => (
                      <a key={i} href={n.href} className="hover:text-[var(--color-primary)] transition-all">
                          {n.label}
                      </a>
                  ))}
              </div>
              <Button className="bg-[var(--color-primary)] text-white font-bold rounded-full px-8 h-12 text-[10px] uppercase tracking-widest hover:brightness-110 transition-all">
                  Join The Collective
              </Button>
          </div>

          <button className="lg:hidden" onClick={() => setMenuOpen(!menuOpen)}>
              {menuOpen ? <X size={32} /> : <Menu size={32} />}
          </button>
      </nav>

      {/* Hero Section - The Biome */}
      {mockData.sectionVisibility.hero && (
      <section className="relative pt-20 pb-40 overflow-hidden">
          <div className="container mx-auto px-6 relative z-10">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
                  <div className="animate-in slide-in-from-left duration-1000">
                      <span className="inline-block text-[var(--color-primary)] font-black tracking-[0.5em] uppercase text-[10px] mb-6">
                        <VisualTextEditable value={mockData.hero.tagline} onSave={(v) => handleUpdate('hero.tagline', v)} isEditMode={!!isEditMode} />
                      </span>
                      <h1 className="text-7xl md:text-9xl font-black font-[var(--font-heading)] leading-[0.9] mb-10 text-[var(--color-text)] tracking-tighter">
                          <VisualTextEditable value={mockData.hero.titlePrefix} onSave={(v) => handleUpdate('hero.titlePrefix', v)} isEditMode={!!isEditMode} /><br/>
                          <span className="text-[var(--color-secondary)] italic">Wisdom.</span>
                      </h1>
                      <p className="text-xl font-medium text-[var(--color-text)]/60 max-w-lg mb-12 leading-relaxed italic">
                          <VisualTextEditable value={mockData.hero.subtitle} onSave={(v) => handleUpdate('hero.subtitle', v)} isEditMode={!!isEditMode} multiline />
                      </p>
                      <div className="flex gap-4">
                          <Button className="h-16 px-10 bg-[var(--color-primary)] text-white rounded-[30%] font-black uppercase tracking-widest text-xs hover:scale-110 transition-transform">
                              Sow Seeds
                          </Button>
                          <Button variant="ghost" className="h-16 px-10 text-[var(--color-text)] rounded-full font-black uppercase tracking-widest text-xs border-2 border-[var(--color-primary)]/20 hover:bg-[var(--color-primary)]/5">
                              View Sanctuary
                          </Button>
                      </div>
                  </div>
                  <div className="relative group">
                      <div className="absolute inset-0 bg-[var(--color-primary)]/5 rounded-[45%] scale-105 transform -rotate-6 animate-pulse" />
                      <div className="relative aspect-square rounded-[40%] overflow-hidden border-[15px] border-white shadow-2xl">
                           <VisualImageEditable 
                              src={mockData.hero.backgroundImage} 
                              className="w-full h-full object-cover grayscale opacity-80 group-hover:grayscale-0 group-hover:opacity-100 transition-all duration-1000" 
                              alt="Ecosystem"
                              onImageChange={(u) => handleUpdate('hero.backgroundImage', u)}
                              isEditMode={!!isEditMode}
                          />
                      </div>
                  </div>
              </div>
          </div>
      </section>
      )}

      {/* Info Cards - Stewardship Nodes */}
      {mockData.sectionVisibility.features && (
      <section id="features" className="py-24">
          <div className="container mx-auto px-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
                  {mockData.features.map((item, i) => (
                      <div key={i} className="text-center group relative p-12 bg-white/40 backdrop-blur-md rounded-[20%] border border-[var(--color-primary)]/10 hover:bg-[var(--color-primary)] hover:text-white transition-all duration-500">
                          <div className={`mx-auto w-20 h-20 rounded-[35%] flex items-center justify-center mb-8 bg-[var(--color-accent)] text-[var(--color-primary)] group-hover:bg-white group-hover:text-[var(--color-primary)] transition-colors`}>
                              {React.createElement(item.icon, { size: 36 })}
                          </div>
                          <h3 className="text-2xl font-black font-[var(--font-heading)] uppercase mb-4 tracking-tight leading-none italic">
                            <VisualTextEditable value={item.title} onSave={(v) => handleUpdate(`features[${i}].title`, v)} isEditMode={!!isEditMode} />
                          </h3>
                          <p className="text-sm opacity-60 leading-relaxed font-medium mb-8">
                            <VisualTextEditable value={item.desc} onSave={(v) => handleUpdate(`features[${i}].desc`, v)} isEditMode={!!isEditMode} multiline />
                          </p>
                          {isEditMode && <button onClick={() => removeArrayItem('features', i)} className="text-[10px] font-bold opacity-30 hover:opacity-100 uppercase tracking-widest">Remove</button>}
                      </div>
                  ))}
                  {isEditMode && (
                      <div onClick={() => addArrayItem('features', { title: "NEW PATH", desc: "Eco module description.", icon: "Leaf" })} className="border-4 border-dashed border-[var(--color-primary)]/20 rounded-[20%] flex flex-col items-center justify-center p-12 cursor-pointer hover:bg-[var(--color-primary)]/5 transition-all text-gray-300">
                          <Plus size={48} />
                      </div>
                  )}
              </div>
          </div>
      </section>
      )}

      {/* Stats - Natural Rhythms */}
      {mockData.sectionVisibility.stats && (
      <section className="py-32 container mx-auto px-6 border-b border-[var(--color-primary)]/10">
          <div className="flex flex-wrap justify-between items-center gap-12">
              {mockData.stats.map((s, i) => (
                  <div key={i} className="group">
                      <div className="text-6xl md:text-8xl font-black text-[var(--color-primary)] mb-2 font-[var(--font-heading)] tracking-tighter italic">
                        <VisualTextEditable value={s.value} onSave={(v) => handleUpdate(`stats[${i}].value`, v)} isEditMode={!!isEditMode} />
                      </div>
                      <div className="text-[10px] font-black uppercase tracking-[0.5em] text-[var(--color-text)]/40 group-hover:text-[var(--color-primary)] transition-all">
                        <VisualTextEditable value={s.label} onSave={(v) => handleUpdate(`stats[${i}].label`, v)} isEditMode={!!isEditMode} />
                      </div>
                  </div>
              ))}
          </div>
      </section>
      )}

      {/* Programs - The Growth Channels */}
      {mockData.sectionVisibility.programs && (
      <section id="programs" className="py-40">
          <div className="container mx-auto px-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-24 items-center">
                  <div className="order-2 lg:order-1 flex gap-4">
                      {mockData.programs.map((p, i) => (
                          <div key={i} className="flex-1 group sticky top-40 hover:-translate-y-8 transition-transform duration-700">
                               <div className="aspect-[3/4] rounded-[50px] overflow-hidden border-[10px] border-white shadow-xl relative">
                                    <VisualImageEditable 
                                      src={p.img} alt={p.title}
                                      className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-all duration-1000"
                                      onImageChange={(u) => handleUpdate(`programs[${i}].img`, u)}
                                      isEditMode={!!isEditMode}
                                  />
                               </div>
                               <div className="mt-8 p-6 bg-white rounded-3xl shadow-sm">
                                   <div className="text-[10px] font-bold text-[var(--color-primary)] uppercase tracking-widest mb-2 italic">{p.age}</div>
                                   <h3 className="text-2xl font-black font-[var(--font-heading)] uppercase mb-4 italic">
                                     <VisualTextEditable value={p.title} onSave={(v) => handleUpdate(`programs[${i}].title`, v)} isEditMode={!!isEditMode} />
                                   </h3>
                                   <p className="text-xs text-[var(--color-text)]/60 font-medium leading-relaxed">
                                     <VisualTextEditable value={p.desc} onSave={(v) => handleUpdate(`programs[${i}].desc`, v)} isEditMode={!!isEditMode} multiline />
                                   </p>
                               </div>
                          </div>
                      ))}
                  </div>
                  <div className="order-1 lg:order-2">
                       <span className="text-[var(--color-secondary)] font-black tracking-[0.5em] uppercase text-xs mb-6 block">GROWTH MODULES</span>
                       <h2 className="text-6xl md:text-8xl font-black font-[var(--font-heading)] text-[var(--color-text)] leading-[0.85] tracking-tighter italic uppercase mb-12">Cultivating <br/> <span className="text-[var(--color-primary)]">Consciousness.</span></h2>
                       <p className="text-2xl font-medium text-[var(--color-text)]/60 italic mb-12 max-w-lg leading-snug border-l-4 border-[var(--color-primary)]/20 pl-10">We don't teach students what to think, but how to listen to the natural intelligence that exists in every cell and leaf.</p>
                       <Button variant="ghost" className="p-0 text-[10px] font-black uppercase tracking-widest flex items-center gap-6 group">Full Syllabus <div className="w-12 h-0.5 bg-[var(--color-primary)] group-hover:w-20 transition-all" /></Button>
                  </div>
              </div>
          </div>
      </section>
      )}

      {/* Facilities - The Sanctuaries */}
      {mockData.sectionVisibility.facilities && (
      <section className="py-40 bg-[var(--color-primary)]/5">
           <div className="container mx-auto px-6 grid grid-cols-1 md:grid-cols-2 gap-12">
                {mockData.facilities.map((fac, i) => (
                    <div key={i} className="bg-white rounded-[60px] p-12 md:p-20 relative group overflow-hidden shadow-sm flex flex-col md:flex-row gap-12">
                        <div className="w-full md:w-1/2 aspect-square rounded-[40px] overflow-hidden order-2 md:order-1">
                             <VisualImageEditable 
                                src={fac.img} alt={fac.title} 
                                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-[3s]" 
                                onImageChange={(u) => handleUpdate(`facilities[${i}].img`, u)} 
                                isEditMode={!!isEditMode}
                            />
                        </div>
                        <div className="w-full md:w-1/2 flex flex-col justify-center order-1 md:order-2">
                            <div className="w-16 h-16 bg-[var(--color-accent)] rounded-[35%] flex items-center justify-center mb-10 text-[var(--color-primary)]">
                                {React.createElement(fac.icon, { size: 32 })}
                            </div>
                            <h3 className="text-3xl font-black font-[var(--font-heading)] mb-6 italic uppercase tracking-tighter">
                                <VisualTextEditable value={fac.title} onSave={(v) => handleUpdate(`facilities[${i}].title`, v)} isEditMode={!!isEditMode} />
                            </h3>
                            <p className="text-lg font-light text-[var(--color-text)]/50 leading-relaxed italic">
                                <VisualTextEditable value={fac.desc} onSave={(v) => handleUpdate(`facilities[${i}].desc`, v)} isEditMode={!!isEditMode} multiline />
                            </p>
                        </div>
                    </div>
                ))}
           </div>
      </section>
      )}

      {/* Gallery - Nature Archive */}
      {mockData.sectionVisibility.gallery && (
      <section id="gallery" className="py-40">
           <div className="container mx-auto px-6">
                <div className="columns-1 md:columns-3 gap-12 space-y-12">
                     {mockData.gallery.map((item, i) => (
                         <div key={i} className="break-inside-avoid group relative rounded-[50px] overflow-hidden border-[10px] border-white shadow-xl hover:-rotate-2 transition-all cursor-pointer">
                              <VisualImageEditable 
                                src={item.img} alt={item.title}
                                className="w-full h-full object-cover grayscale brightness-90 group-hover:grayscale-0 group-hover:brightness-100 transition-all duration-1000"
                                onImageChange={(u) => handleUpdate(`gallery[${i}].img`, u)}
                                isEditMode={!!isEditMode}
                            />
                              <div className="absolute inset-x-8 bottom-8 p-6 bg-white/70 backdrop-blur-md rounded-3xl opacity-0 group-hover:opacity-100 transform translate-y-4 group-hover:translate-y-0 transition-all">
                                  <h4 className="text-xl font-black uppercase italic font-[var(--font-heading)] leading-none mb-1">{item.title}</h4>
                                  <p className="text-[10px] font-bold text-[var(--color-primary)] uppercase tracking-widest">{item.category}</p>
                              </div>
                         </div>
                     ))}
                </div>
           </div>
      </section>
      )}

      {/* News - Seasonal Dispatch */}
      {mockData.sectionVisibility.news && (
      <section className="py-40 bg-[var(--color-secondary)]/5 px-6">
          <div className="container mx-auto">
              <div className="max-w-3xl mb-32">
                  <h2 className="text-6xl md:text-[10rem] font-black font-[var(--font-heading)] text-[var(--color-text)] leading-[0.75] tracking-[calc(-0.06em)] italic uppercase">The <br/> <span className="text-[var(--color-primary)]">Dispatch.</span></h2>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-24">
                  {mockData.news.map((item, i) => (
                      <div key={i} className="group cursor-pointer">
                          <div className="flex gap-8 items-end mb-12">
                               <div className="text-8xl font-black text-[var(--color-primary)] opacity-10">0{i+1}</div>
                               <div className="text-[10px] font-black uppercase tracking-[0.4em] text-[var(--color-primary)] border-b border-[var(--color-primary)]/20 pb-2 flex-1">RELEASED // {item.date}</div>
                          </div>
                          <h3 className="text-4xl font-black font-[var(--font-heading)] text-gray-900 mb-8 leading-none italic uppercase tracking-tighter">
                            <VisualTextEditable value={item.title} onSave={(v) => handleUpdate(`news[${i}].title`, v)} isEditMode={!!isEditMode} />
                          </h3>
                          <div className="aspect-video overflow-hidden mb-12 rounded-[50px] border-[15px] border-white shadow-xl">
                               <VisualImageEditable 
                                  src={item.img} alt={item.title}
                                  className="w-full h-full object-cover grayscale brightness-110 group-hover:grayscale-0 transition-all duration-1000"
                                  onImageChange={(u) => handleUpdate(`news[${i}].img`, u)}
                                  isEditMode={!!isEditMode}
                              />
                          </div>
                          <p className="text-2xl font-medium text-[var(--color-text)]/40 leading-snug mb-12 line-clamp-2 max-w-xl italic group-hover:text-[var(--color-text)] transition-colors">
                              <VisualTextEditable value={item.desc} onSave={(v) => handleUpdate(`news[${i}].desc`, v)} isEditMode={!!isEditMode} multiline />
                          </p>
                          <Button variant="ghost" className="p-0 text-[10px] font-black uppercase tracking-widest text-[var(--color-primary)] hover:bg-transparent flex items-center gap-6">Read Communiqué <ArrowRight size={18} /></Button>
                      </div>
                  ))}
              </div>
          </div>
      </section>
      )}

      {/* Downloads - Organic Archive */}
      {mockData.sectionVisibility.downloads && (
      <section className="py-24 bg-white/40 border-y border-[var(--color-primary)]/10 px-6">
          <div className="container mx-auto">
              <div className="flex items-center gap-10 mb-20">
                   <h2 className="text-xs font-black uppercase tracking-[0.6em] text-[var(--color-text)]/40">
                       <VisualTextEditable 
                           value={mockData.labels.downloadsSection} 
                           onSave={(v) => handleUpdate('labels.downloadsSection', v)} 
                           isEditMode={!!isEditMode} 
                           as="span" 
                       />
                   </h2>
                   <div className="h-0.5 flex-1 bg-[var(--color-primary)]/10" />
                   <Leaf className="text-[var(--color-primary)] opacity-20" size={24} />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
                  {mockData.downloads.map((doc, i) => (
                      <div key={i} className="group relative">
                          <div className="absolute inset-0 bg-[var(--color-accent)] rounded-[30%] -rotate-2 group-hover:rotate-2 transition-transform duration-500" />
                          <div className="relative bg-white p-10 rounded-[30%] border border-[var(--color-primary)]/5 shadow-sm hover:shadow-xl transition-all h-full flex flex-col items-center text-center">
                              <FileText size={48} className="text-[var(--color-primary)]/20 mb-8 group-hover:scale-110 transition-transform" />
                              <h4 className="text-sm font-black uppercase italic tracking-tight text-[var(--color-text)] mb-4">
                                <VisualTextEditable value={doc.title} onSave={(v) => handleUpdate(`downloads[${i}].title`, v)} isEditMode={!!isEditMode} />
                              </h4>
                              <p className="text-[10px] font-bold text-[var(--color-text)]/30 uppercase tracking-widest italic mb-10">
                                <VisualTextEditable value={doc.type} onSave={(v) => handleUpdate(`downloads[${i}].type`, v)} isEditMode={!!isEditMode} as="span" /> // 
                                <VisualTextEditable value={doc.size} onSave={(v) => handleUpdate(`downloads[${i}].size`, v)} isEditMode={!!isEditMode} as="span" />
                              </p>
                              <div className="mt-auto w-full pt-8 border-t border-[var(--color-primary)]/5 flex justify-between items-center px-4">
                                   <button className="text-[10px] font-black text-[var(--color-primary)] uppercase underline underline-offset-4 decoration-[var(--color-accent)] decoration-4">Gather</button>
                                   <Download size={20} className="text-[var(--color-primary)] opacity-20" />
                              </div>
                          </div>
                          {isEditMode && <button onClick={() => removeArrayItem('downloads', i)} className="absolute top-4 right-4 z-20 text-[9px] font-black text-red-400 opacity-20 hover:opacity-100 uppercase">Drop</button>}
                      </div>
                  ))}
                  {isEditMode && (
                      <div onClick={() => addArrayItem('downloads', { title: "NEW_LEAF.pdf", size: "1.2 MB", type: "PDF" })} className="aspect-square border-4 border-dashed border-[var(--color-primary)]/10 rounded-[30%] flex items-center justify-center cursor-pointer hover:bg-white transition-all text-[var(--color-primary)]/20 hover:text-[var(--color-primary)]">
                          <Plus size={48} />
                      </div>
                  )}
              </div>
          </div>
      </section>
      )}

      {/* CTA - Global Invitation */}
      {mockData.sectionVisibility.cta && (
      <section className="py-64 relative bg-[var(--color-primary)] text-white text-center px-6 overflow-hidden">
          <div className="absolute inset-0 opacity-10 flex items-center justify-center">
              <Leaf size={800} strokeWidth={0.5} className="rotate-45" />
          </div>
          <div className="container mx-auto relative z-10 max-w-5xl">
              <span className="text-[var(--color-accent)] font-black tracking-[0.6em] uppercase text-xs mb-10 block">Nurture Your Growth</span>
              <h2 className="text-7xl md:text-[12rem] font-black font-[var(--font-heading)] leading-none italic mb-16 uppercase tracking-tighter">
                 Return to <br/> <span className="underline underline-offset-[20px] decoration-[var(--color-accent)]">Nature.</span>
              </h2>
              <div className="flex flex-col sm:flex-row justify-center gap-10">
                   <Button className="bg-[var(--color-accent)] text-[var(--color-primary)] rounded-[35%] h-24 px-16 font-black uppercase tracking-widest text-xs hover:scale-105 transition-all shadow-2xl">
                       Plant Your Seed
                   </Button>
                   <Button variant="outline" className="border-white/20 text-white rounded-full h-24 px-16 font-black uppercase tracking-widest text-xs hover:bg-white hover:text-[var(--color-primary)] transition-all">
                       Explore Sanctuaries
                   </Button>
              </div>
          </div>
      </section>
      )}

      {/* Footer - Final Coordinates */}
      <footer id="contact" className="bg-[var(--color-background)] pt-40 pb-20 px-6 border-t border-[var(--color-primary)]/10">
          <div className="container mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-24 mb-40 items-start">
                  <div className="lg:col-span-6">
                      <div className="flex items-center gap-6 mb-12">
                           <div className="w-16 h-16 bg-[var(--color-primary)]/10 rounded-[40%] flex items-center justify-center p-3">
                               <Leaf className="text-[var(--color-primary)]" size={32} />
                           </div>
                           <h2 className="text-5xl font-black italic uppercase tracking-tighter font-[var(--font-heading)]">{schoolName}</h2>
                      </div>
                      <p className="text-3xl font-medium leading-tight mb-20 italic text-[var(--color-text)]/20 max-w-lg">Protecting the natural world by educating the natural mind.</p>
                      <div className="flex gap-4">
                          {[Facebook, Instagram, Youtube, Linkedin].map((Icon, i) => (
                              <a key={i} href="#" className="w-16 h-16 bg-white border border-[var(--color-primary)]/10 rounded-[40%] flex items-center justify-center hover:bg-[var(--color-primary)] hover:text-white transition-all shadow-sm"><Icon size={24} /></a>
                          ))}
                      </div>
                  </div>
                  
                  <div className="lg:col-span-2">
                      <h4 className="text-[11px] font-black tracking-[0.4em] uppercase text-[var(--color-primary)] mb-16 underline underline-offset-8">DIRECTORY</h4>
                      <ul className="space-y-8 text-sm font-black uppercase tracking-tighter italic">
                          {mockData.nav.map((item, i) => (
                              <li key={i}><a href={item.href} className="hover:text-[var(--color-primary)] transition-all flex items-center gap-8 group">
                                <div className="w-2 h-2 rounded-full bg-[var(--color-primary)] opacity-0 group-hover:opacity-100" />
                                {item.label}
                                </a></li>
                          ))}
                      </ul>
                  </div>

                  <div className="lg:col-span-4">
                      <h4 className="text-[11px] font-black tracking-[0.4em] uppercase text-[var(--color-primary)] mb-16 underline underline-offset-8">THE ROOTS</h4>
                      <div className="space-y-16">
                          <div className="flex gap-10 group">
                              <MapPin size={32} className="text-[var(--color-primary)] opacity-20 shrink-0 group-hover:opacity-100 transition-all" />
                              <p className="text-xl font-black italic tracking-tighter leading-none opacity-40 group-hover:opacity-100">
                                  <VisualTextEditable value={mockData.contact.address} onSave={(v) => handleUpdate('contact.address', v)} isEditMode={!!isEditMode} multiline />
                              </p>
                          </div>
                          <div className="flex gap-10 group">
                              <Phone size={32} className="text-[var(--color-primary)] opacity-20 shrink-0 group-hover:opacity-100 transition-all" />
                              <p className="text-xl font-black italic tracking-tighter opacity-40 group-hover:opacity-100">
                                  <VisualTextEditable value={mockData.contact.phone} onSave={(v) => handleUpdate('contact.phone', v)} isEditMode={!!isEditMode} />
                              </p>
                          </div>
                      </div>
                  </div>
              </div>
              
              <div className="pt-20 border-t border-[var(--color-primary)]/10 flex flex-col md:flex-row justify-between items-center gap-8 text-[10px] font-black uppercase tracking-[0.5em] text-[var(--color-text)]/20">
                  <p>&copy; {new Date().getFullYear()} {schoolName} // BIOSPHERIC_RECORD // REGENERATIVE_LICENSE</p>
                  <div className="flex gap-12 italic underline underline-offset-8">
                      <a href="#" className="hover:text-[var(--color-primary)]">Tree Charter</a>
                      <a href="#" className="hover:text-[var(--color-primary)]">Water Rights</a>
                  </div>
              </div>
          </div>
      </footer>

      {/* Organic Full UI Overlay */}
      {menuOpen && (
          <div className="fixed inset-0 z-[1000] bg-[var(--color-background)] p-12 flex flex-col animate-in fade-in zoom-in duration-500 overflow-hidden">
               {/* Texture Background */}
               <div className="absolute inset-0 opacity-10 bg-[url('https://www.transparenttextures.com/patterns/natural-paper.png')]" />
               
               <div className="container mx-auto h-full flex flex-col relative z-20">
                   <div className="flex justify-between items-center mb-24 border-b border-[var(--color-primary)]/10 pb-12">
                       <h2 className="text-4xl font-black italic uppercase tracking-tighter leading-none text-[var(--color-primary)]">{schoolName}</h2>
                       <button onClick={() => setMenuOpen(false)} className="text-[var(--color-text)]"><X size={64} /></button>
                   </div>
                   <nav className="flex flex-col gap-10">
                       {mockData.nav.filter(n => n.visible).map((n, i) => (
                           <a key={i} href={n.href} onClick={() => setMenuOpen(false)} className="text-7xl md:text-9xl font-black uppercase tracking-tighter italic text-[var(--color-text)]/10 hover:text-[var(--color-primary)] hover:translate-x-12 transition-all">
                               {n.label}
                           </a>
                       ))}
                   </nav>
                   <div className="mt-auto flex justify-between items-end border-t border-[var(--color-primary)]/10 pt-12">
                        <p className="text-[10px] font-black uppercase tracking-[0.8em] text-[var(--color-secondary)]">SOWING_THE_FUTURE</p>
                        <div className="text-[15rem] font-black text-[var(--color-primary)] opacity-[0.02] leading-none">ECO</div>
                   </div>
               </div>
          </div>
      )}
    </div>
  );
};

export default EcoFriendly;
